/**
 * Notification bell (F3-C): unread counter (local query) + dropdown with
 * recent ones; mark-as-read on click (write-path read_at) and deep-link by
 * kind. Local-first: everything resolves offline.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Bell } from "@fleet/icons";
import { markNotificationReadMutation } from "@space/shared";
import type { Notification } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { Link, useNavigate } from "@tanstack/solid-router";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useMyNotifications, useUnreadNotifications } from "../../hooks/use-community-data";
import { notificationView } from "./notification-meta";

/** Maximum shown in the badge before "9+". */
const BADGE_CAP = 9;

export const NotificationsBell: Component<{
  /**
   * Layout slot where the bell is mounted. The layout mounts it THREE times
   * (desktop sidebar + mobile bar + the copy of the sidebar that lives in the
   * mobile drawer, which is always mounted so its close can be animated).
   * All of them coexist in the DOM: the ones not in play are simply hidden by
   * CSS, and `display:none` doesn't remove a node from the document. Testids
   * must be UNIQUE per mount (Playwright strict mode), so every slot other
   * than desktop suffixes them.
   */
  slot?: "desktop" | "mobile" | "drawer";
}> = (props) => {
  const tid = (name: string) =>
    props.slot && props.slot !== "desktop" ? `${name}-${props.slot}` : name;
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const navigate = useNavigate();
  const unread = useUnreadNotifications();
  const recent = useMyNotifications();
  const [open, setOpen] = createSignal(false);

  const unreadCount = createMemo(() => (unread.data ?? []).length);

  const onPick = (n: Notification) => {
    const db = drizzle();
    if (db && !n.read_at) {
      // `patchRowVerified` can throw; fire-and-forget call.
      void markNotificationReadMutation(db, n.id).catch((err: unknown) => {
        console.error("Error marking notification as read:", err);
      });
    }
    setOpen(false);
    void navigate({ to: notificationView(n).to });
  };

  return (
    // Anchored to the source `slot`, but WITHOUT positioning by hand: the panel
    // used to be placed with `absolute right-0` + `w-80`, i.e. it pinned its
    // right edge to the button's and grew 320px to the left. On the mobile bar
    // that's fine (the button lives on the right), but on DESKTOP the bell is
    // in the LEFT sidebar, so the panel spilled out of the viewport and ended
    // up half cut off. Kobalte repositions against the edges (flip/shift),
    // which a fixed calculation cannot do: the correct answer depends on
    // where the trigger is and how big the window happens to be at that moment.
    <Popover
      open={open()}
      onOpenChange={setOpen}
      placement={props.slot === "mobile" ? "bottom-end" : "bottom-start"}
      gutter={8}
      flip
      // So it never kisses the window edge even if it has to shift.
      overflowPadding={12}
    >
      <PopoverTrigger
        data-testid={tid("notifications-bell")}
        aria-label={t(msg`Notifications`)}
        class="relative rounded-xl p-2 text-muted-foreground transition-colors hover:bg-accent/10 hover:text-accent"
      >
        <Bell class="h-5 w-5" />
        <Show when={unreadCount() > 0}>
          <span
            data-testid={tid("notifications-badge")}
            class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-danger-foreground"
          >
            {unreadCount() > BADGE_CAP ? `${BADGE_CAP}+` : unreadCount()}
          </span>
        </Show>
      </PopoverTrigger>

      <PopoverContent
        data-testid={tid("notifications-dropdown")}
        // The width is also clamped to the window: on narrow screens a fixed
        // 20rem overflows again on the other side, and then no repositioning
        // can save it.
        class="max-h-96 w-[min(20rem,calc(100vw-1.5rem))] overflow-y-auto rounded-2xl border border-border bg-surface p-2 shadow-xl"
      >
        <div class="flex items-center justify-between px-2 py-1.5">
          <span class="text-sm font-bold">{t(msg`Notifications`)}</span>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            class="text-xs text-accent hover:underline"
          >
            {t(msg`See all`)}
          </Link>
        </div>
        <ul class="space-y-1">
          <For
            each={(recent.data ?? []).slice(0, 8)}
            fallback={
              <li class="px-2 py-3 text-center text-xs text-muted-foreground">
                {t(msg`No notifications yet.`)}
              </li>
            }
          >
            {(n) => (
              <li>
                <button
                  type="button"
                  data-testid={tid("notification-item")}
                  data-unread={n.read_at ? "false" : "true"}
                  onClick={() => onPick(n)}
                  class="flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left text-sm hover:bg-accent/10"
                  classList={{ "font-semibold": !n.read_at }}
                >
                  <Show when={!n.read_at}>
                    <span class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent" />
                  </Show>
                  <span class="flex-1">{t(notificationView(n).title)}</span>
                </button>
              </li>
            )}
          </For>
        </ul>
      </PopoverContent>
    </Popover>
  );
};
